/* ==========================================================================
   Build your garage — tiered bundle builder

   Sits on every product page, directly under the title block. The shopper
   picks a tier (1, 2, 3 or 4 clocks); from 2 up, one model-picker row per
   clock appears, and the button and summary follow live
   ("Add 3 to Cart · $134.99"). The same pricing feeds the cart, so the
   drawer and the builder always agree.

   Pricing is read from the active market (markets.js `bundles`) and done in
   whole cents, so per-clock prices and savings are exact:

     1 clock  $59.99                       ($59.99 each)
     2 clocks $99.99   save $19.99          ($50.00 each)
     3 clocks $134.99  save $44.98 + mystery gift ($45.00 each)
     4 clocks $169.99  save $69.97          ($42.50 each)

   Savings are measured against buying the same clocks one at a time at the
   single price, never against the compare-at price. Orders above four
   stack whole four-packs plus the matching tier for the rest (5 = 4 + 1).

   SHOPIFY — where the real pieces plug in (no app needed):
   1. Pricing: three automatic discounts on the four clock products,
      "Amount off order" with a minimum quantity of 2 / 3 / 4 items
      ($19.99 / $44.98 / $69.97 off), set not to combine with each other.
      Shopify applies the best one; this file only displays the result.
      (5+ clocks then get the 4-clock saving; see PRODUCT_SETUP.md.)
   2. Add to cart: replace STORE.cart.addMany() with one POST to
      /cart/add.js carrying every clock as its own item, e.g.
        { items: [{ id: <variant id>, quantity: 1,
                    properties: { _garage: '3', _garage_slot: '1 of 3' } }] }
      Underscore-prefixed line-item properties stay hidden at checkout but
      are visible to the discount / bundle app and on the order.
   3. Mystery gift: a hidden $0 "Mystery gift" product. When the cart
      reaches 3 clocks, POST it to /cart/add.js (and remove it below 3);
      a cart-validation rule or a buy-3-get-1 automatic discount keeps
      it from being bought on its own.
   ========================================================================== */

window.STORE = window.STORE || {};

STORE.bundle = (function () {
  'use strict';

  var GIFT_FROM = 3;          // free gift at 3+ clocks
  var MAX_TIER = 4;

  /* ---------- pricing (cents) --------------------------------------------- */

  function table() {
    var M = STORE.markets;
    var L = STORE.localization;
    var code = (L && L.country) || M.defaultCountry;
    var entry = M.countries[code] || M.countries[M.defaultCountry];
    return entry[1];
  }

  function cents(value) {
    return Math.round(parseFloat(value) * 100);
  }

  /* single-clock price: every model shares it, so read the first one */
  function singleCents() {
    var prices = table().prices;
    var first = Object.keys(prices)[0];
    return cents(prices[first][0]);
  }

  function tierCents(units) {
    if (units <= 0) return 0;
    if (units === 1) return singleCents();
    var b = table().bundles || {};
    return b[units] ? cents(b[units]) : singleCents() * units;
  }

  /* any quantity: whole four-packs, then the tier for what is left */
  function totalCents(units) {
    var packs = Math.floor(units / MAX_TIER);
    var rest = units % MAX_TIER;
    return packs * tierCents(MAX_TIER) + tierCents(rest);
  }

  function savingsCents(units) {
    return singleCents() * units - totalCents(units);
  }

  function money(c) {
    return STORE.localization.formatMoney((c / 100).toFixed(2));
  }

  function hasGift(units) {
    return units >= GIFT_FROM;
  }

  /* ---------- the builder ------------------------------------------------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function maker(title) {
    return String(title).split(' ')[0];
  }

  function initBuilder(root) {
    var P = STORE.products;
    var handles = Object.keys(P);
    var home = root.getAttribute('data-garage');
    var form = root.closest('form');
    var tiers = Array.prototype.slice.call(root.querySelectorAll('[data-tier]'));
    var picks = root.querySelector('[data-garage-picks]');
    var rowsBox = root.querySelector('[data-garage-rows]');
    var list = root.querySelector('[data-garage-list]');
    var gift = root.querySelector('[data-garage-gift]');
    var buttons = document.querySelectorAll('[data-garage-label]');

    /* clock 1 is the page's own model; extra clocks start on the others from
       the same category (supercars or F1), then the rest - so the garage
       reads as a mix, and every one of them can be changed */
    var cat = (P[home] || {}).category;
    var order = [home]
      .concat(handles.filter(function (h) { return h !== home && P[h].category === cat; }))
      .concat(handles.filter(function (h) { return P[h].category !== cat; }));
    var state = { units: 1, picks: order.slice(0, MAX_TIER) };

    function paintTiers() {
      tiers.forEach(function (t) {
        var n = parseInt(t.getAttribute('data-tier'), 10);
        var on = n === state.units;
        t.classList.toggle('is-active', on);
        var input = t.querySelector('input');
        if (input) input.checked = on;
        var total = t.querySelector('[data-garage-total]');
        var unit = t.querySelector('[data-garage-unit]');
        var save = t.querySelector('[data-garage-save]');
        if (total) total.textContent = money(totalCents(n));
        if (unit) unit.textContent = money(Math.round(totalCents(n) / n)) + ' per clock';
        if (save) {
          var s = savingsCents(n);
          save.textContent = s > 0 ? 'Save ' + money(s) : 'Just this one';
        }
      });
    }

    function row(i) {
      var n = i + 1;
      var chosen = state.picks[i];
      var html = '<div class="garage__row" role="radiogroup" aria-label="Clock ' + n + ' model">' +
        '<p class="garage__row-head"><span>Clock ' + n + '</span><b data-row-name>' + esc(P[chosen].title) + '</b></p>' +
        '<div class="garage__swatches">';
      handles.forEach(function (h) {
        var p = P[h];
        var on = h === chosen;
        html += '<label class="garage__swatch ' + esc(p.theme) + (on ? ' is-active' : '') + '" title="' + esc(p.title) + '">' +
          '<input class="visually-hidden" type="radio" name="garage-pick-' + n + '" value="' + esc(h) + '"' +
          (on ? ' checked' : '') + ' data-pick="' + i + '">' +
          '<i aria-hidden="true"></i><span>' + esc(maker(p.title)) + '</span>' +
          '<span class="visually-hidden"> ' + esc(p.title.split(' ').slice(1).join(' ')) + '</span>' +
        '</label>';
      });
      return html + '</div></div>';
    }

    function paintRows() {
      if (!picks || !rowsBox) return;
      picks.hidden = state.units < 2;
      if (state.units < 2) { rowsBox.innerHTML = ''; return; }
      var html = '';
      for (var i = 0; i < state.units; i++) html += row(i);
      rowsBox.innerHTML = html;
    }

    function chosen() {
      return state.picks.slice(0, state.units);
    }

    function counts() {
      var out = {};
      chosen().forEach(function (h) { out[h] = (out[h] || 0) + 1; });
      return out;
    }

    function paintSummary() {
      var c = counts();
      if (list) {
        list.innerHTML = Object.keys(c).map(function (h) {
          return '<li class="' + esc(P[h].theme) + '"><span class="swatch" aria-hidden="true"></span>' +
            c[h] + '× ' + esc(P[h].title) + '</li>';
        }).join('');
      }
      if (gift) gift.hidden = !hasGift(state.units);
      var label = (state.units === 1 ? 'Add to Cart' : 'Add ' + state.units + ' to Cart') +
        ' · ' + money(totalCents(state.units));
      Array.prototype.forEach.call(buttons, function (b) {
        var short = b.getAttribute('data-garage-label');
        b.textContent = short ? short + ' · ' + money(totalCents(state.units)) : label;
      });
    }

    function paint() {
      paintTiers();
      paintRows();
      paintSummary();
    }

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'garage-units') {
        state.units = parseInt(t.value, 10) || 1;
        paint();
        return;
      }
      if (t.hasAttribute('data-pick')) {
        var i = parseInt(t.getAttribute('data-pick'), 10);
        state.picks[i] = t.value;
        var r = t.closest('.garage__row');
        Array.prototype.forEach.call(r.querySelectorAll('.garage__swatch'), function (s) {
          s.classList.toggle('is-active', s.contains(t));
        });
        var name = r.querySelector('[data-row-name]');
        if (name) name.textContent = P[t.value].title;
        paintSummary();
      }
    });

    /* submit: the form button, or the sticky bar's "Buy Now" */
    function submit() {
      STORE.cart.addMany(counts());
    }
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submit();
      }, true);
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-garage-buy]')) { e.preventDefault(); submit(); }
    });

    if (STORE.localization && STORE.localization.onChange) STORE.localization.onChange(paint);
    paint();
  }

  /* any element with data-bundle-price="<units>" shows that tier's total,
     data-bundle-save="<units>" its saving, data-bundle-each="<units>" the
     per-clock price - used by the homepage and collection bundle blocks */
  function paintStatic() {
    function each(attr, fn) {
      Array.prototype.forEach.call(document.querySelectorAll('[' + attr + ']'), function (el) {
        var n = parseInt(el.getAttribute(attr), 10);
        if (n) el.textContent = fn(n);
      });
    }
    each('data-bundle-price', function (n) { return money(totalCents(n)); });
    each('data-bundle-save', function (n) { return money(savingsCents(n)); });
    each('data-bundle-each', function (n) { return money(Math.round(totalCents(n) / n)); });
  }

  function init() {
    paintStatic();
    Array.prototype.forEach.call(document.querySelectorAll('[data-garage]'), initBuilder);
    if (STORE.localization && STORE.localization.onChange) STORE.localization.onChange(paintStatic);
  }

  return {
    init: init,
    totalCents: totalCents,
    savingsCents: savingsCents,
    singleCents: singleCents,
    hasGift: hasGift,
    giftFrom: GIFT_FROM,
  };
}());
